// Moved from `src/hooks/mutation/useMutateDeleteInvitation.ts` (now the app
// delegate) with the OT4 mutation slice (ARCFE-1033).
//
// Severed per ARCFE-921: both toasts and the Rematch `trackDeleteTeamMember`
// dispatch. The `onSettled` COMMERCE call — `invalidateCurrentTeamBillingInfo`
// — crosses as `onInvitationSettled` and is sequenced by the delegate: per
// delivery section 3 the owning domain exports the command and app integration
// sequences it. It is deliberately NOT injected through `configureWorkspaceData`;
// that shape hides an edge the manifest cannot see (design D7).
//
// The `onMutate` optimistic filter stays here — it rewrites only the
// workspace-owned team-details cache.
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { GET_TEAM_DETAILS, type TeamResponse } from '../../contracts';
import { deleteTeamMember } from '../api/teams';
import { isTransportError, type WorkspaceHttpError } from '../config';

export const DELETE_INVITATION = 'deleteInvitation';

export interface UseMutateDeleteInvitationOptions {
  /** App-reaction port: fired where the Rematch dispatch + success toast fired. */
  onInvitationDeleted?: () => void;
  /** App-reaction port: fired where the legacy destructive toast fired. */
  onDeleteError?: (error: WorkspaceHttpError) => void;
  /**
   * App-reaction port fired at the END of `onSettled`, where the COMMERCE
   * `invalidateCurrentTeamBillingInfo(teamId)` call sat — after the team-details
   * invalidation, preserving the frozen ordering.
   */
  onInvitationSettled?: (teamId: string) => void;
}

/** DELETE /teams/{team_id}/members/{member_id} */
export function useMutateDeleteInvitation({
  teamId,
  memberId,
  ...options
}: {
  teamId: string;
  memberId: string;
} & UseMutateDeleteInvitationOptions) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [DELETE_INVITATION, { teamId, memberId }],
    mutationFn: async () => {
      return await deleteTeamMember(teamId, memberId);
    },
    onMutate: () => {
      void queryClient.cancelQueries({ queryKey: [GET_TEAM_DETAILS, { teamId }] });
      void queryClient.setQueryData<TeamResponse>(
        [GET_TEAM_DETAILS, { teamId }],
        (oldData) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            team: {
              ...oldData.team,
              team_members: oldData.team.team_members?.filter(
                (member) => member.id !== memberId
              ),
            },
          };
        }
      );
    },
    onSuccess: () => {
      options?.onInvitationDeleted?.();
    },
    onError: (error) => {
      if (isTransportError(error)) {
        options?.onDeleteError?.(error);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: [GET_TEAM_DETAILS, { teamId }],
      });
      options?.onInvitationSettled?.(teamId);
    },
  });
}
